import React from "react";
import { Box, Flex, Grid, Image } from "theme-ui";

import { CountryStates } from "helpers/CountryStates";

import LogoUpload from "./LogoUpload";

import BasicButton from "components/forms/BasicButton";
import TextInput from "components/forms/TextInput";
import H3 from "components/typography/H3";
import SelectInput from "components/forms/SelectInput";
import Separator from "components/ui/Separator";
import Subtext from "components/typography/Subtext";
import H5 from "components/typography/H5";

export default function GeneralSettings({
  company,
  values,
  errors,
  submitCount,
  setFieldValue,
  initCompany,
}: {
  company: any;
  values: any;
  errors: any;
  submitCount: number;
  setFieldValue: (i: string, v: any) => void;
  initCompany: () => void;
}) {
  const countryStates: any[] = Object.entries(CountryStates["US"]?.states || {}).map(([key, value]) => ({ value: key, label: value.name }));

  return (
    <>
      <Box sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
        <H3 marginTop={"10px"} marginBottom={0} fontSize={"16px"} fontWeight={"600"} fontFamily={"Poppins"}>
          General
        </H3>
      </Box>
      <Separator />
      <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
        <Box>
          <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
            Basic Info
          </H5>
          <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
            Edit Company Name and Phone.
          </Subtext>
        </Box>
        <Flex sx={{ gap: "1rem" }}>
          <TextInput
            name="name"
            label="Company Name"
            value={values.name}
            onChange={(val: any) => setFieldValue("name", val)}
            $errors={errors.name && submitCount > 0 ? errors.name : null}
            $customStyles={{ flexGrow: 2 }}
          />
          <TextInput name="data[phone]" label="Phone" value={values?.data?.phone} onChange={(val: any) => setFieldValue("data.phone", val)} $customStyles={{ flexGrow: 1 }} />
        </Flex>
      </Grid>
      <Separator />
      <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
        <Box>
          <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
            Address
          </H5>
          <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
            Edit Company Address.
          </Subtext>
        </Box>
        <Box>
          <Flex sx={{ gap: "1rem" }}>
            <TextInput
              name="data[address][line1]"
              label="Address 1"
              value={values?.data?.address?.line1}
              onChange={(val: any) => setFieldValue("data.address.line1", val)}
              $customStyles={{ flexGrow: 2 }}
            />
            <TextInput
              name="data[address][line2]"
              label="Address 2"
              value={values?.data?.address?.line2}
              onChange={(val: any) => setFieldValue("data.address.line2", val)}
              $customStyles={{ flexGrow: 1 }}
            />
          </Flex>
          <Flex sx={{ gap: "1rem" }}>
            <TextInput
              name="data[address][city]"
              label="City"
              value={values?.data?.address?.city}
              onChange={(val: any) => setFieldValue("data.address.city", val)}
              $customStyles={{ width: "33.3333%" }}
            />
            <SelectInput
              name="data[address][state]"
              label="State"
              value={values?.data?.address?.state}
              options={countryStates}
              onChange={(val: any) => setFieldValue("data.address.state", val)}
              $customStyles={{ width: "33.3333%" }}
            />
            <TextInput name="data[address][zip]" label="Zip" value={values?.data?.address?.zip} onChange={(val: any) => setFieldValue("data.address.zip", val)} $customStyles={{ width: "33.3333%" }} />
          </Flex>
        </Box>
      </Grid>
      <Separator />
      <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
        <Box>
          <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
            Account Owner
          </H5>
          <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
            Change the registered Account Owner.
          </Subtext>
        </Box>
        <Flex sx={{ gap: "1rem" }}>
          <TextInput
            label="Account Owner First Name"
            value={values.owner?.firstName}
            onChange={(val: any) => setFieldValue("owner.firstName", val)}
            $errors={errors.firstName && submitCount > 0 ? errors.firstName : null}
            $customStyles={{ flexGrow: 1 }}
          />
          <TextInput
            name="lastName"
            label="Account Owner Last Name"
            value={values?.owner?.lastName}
            onChange={(val: any) => setFieldValue("owner.lastName", val)}
            $errors={errors.lastName && submitCount > 0 ? errors.lastName : null}
            $customStyles={{ flexGrow: 1 }}
          />
          <TextInput
            name="email"
            label="Account Owner Email"
            value={values?.owner?.email}
            onChange={(val: any) => setFieldValue("owner.email", val)}
            $errors={errors.email && submitCount > 0 ? errors.email : null}
            $customStyles={{ flexGrow: 1 }}
          />
        </Flex>
      </Grid>
      <Separator />
      <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
        <Box>
          <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
            Company Logo
          </H5>
          <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
            Update your company logo.
          </Subtext>
        </Box>
        <Flex sx={{ gap: "1rem", alignItems: "center" }}>
          {values?.data?.logo && <Image src={values?.data?.logo} width={75} />}
          <LogoUpload company={company} title={values?.data?.logo ? "Replace Logo" : "Upload Logo"} afterUpload={() => initCompany()} />
          {values?.data?.logo && (
            <BasicButton scheme="dangerSecondary" onClick={() => setFieldValue("data.logo", "")}>
              Remove
            </BasicButton>
          )}
        </Flex>
      </Grid>
    </>
  );
}
